import { useMemo } from "react"
import { SupportedTokenName } from "@mezo/api"
import { Modal } from "@mezo-org/mezo-clay"

import {
  RootState,
  selectDepositByDepositId,
  selectTokenDecimals,
  useDappSelector,
} from "#/redux-state"
import FailedActionModalBody from "#/shared/components/modals/FailedActionModalBody"
import { useModalCustomOptions } from "#/shared/hooks"
import { BridgeableTokens, BridgeAssetFormValue } from "#/shared/types"
import { convertDecimalsToTokenDecimals } from "#/shared/utils"

import { BaseModalProps } from "../BaseModal"

import BridgeAssetStepperModal from "./BridgeAssetStepperModal"

type BridgeDepositStepperModalProps = BaseModalProps & {
  depositId: number
  token: SupportedTokenName
  amount: string
}

function BridgeDepositStepperModal({
  depositId,
  token,
  amount,
  ...props
}: BridgeDepositStepperModalProps) {
  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token),
  )

  const contextValue = useMemo<BridgeAssetFormValue>(
    () => ({
      amount: convertDecimalsToTokenDecimals(
        BigInt(amount),
        Number(tokenDecimals),
      ),
      token: token as BridgeableTokens,
      shouldUseAnotherWallet: false,
      depositId,
    }),
    [amount, tokenDecimals, token, depositId],
  )

  return <BridgeAssetStepperModal {...props} contextValue={contextValue} />
}

export default function BridgeDepositModal({ ...props }: BaseModalProps) {
  const { depositId } = useModalCustomOptions<{
    depositId: number
  }>()

  const deposit = useDappSelector((state: RootState) =>
    selectDepositByDepositId(state, depositId),
  )

  if (!deposit) {
    return (
      <Modal {...props}>
        <FailedActionModalBody
          title={`Deposit with id: ${depositId} does not exist`}
          onClickNextStep={() => props?.onClose?.({})}
        />
      </Modal>
    )
  }

  return (
    <BridgeDepositStepperModal
      depositId={deposit.depositId}
      amount={deposit.amount}
      token={deposit.token}
      {...props}
    />
  )
}
